import { useEffect, useState } from 'react';
import Page from '../../../components/page/Page';
import {
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  TextField,
  Button,
  Autocomplete,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from '@mui/material';
import useThemeMediaQuery from '../../../../core/hooks/useThemeMediaQuery';
import { fetchContainerAudits } from '../../../../core/modules/auditoria-contenedores/services/api';
import { ContainerAudit } from '../../../../core/modules/auditoria-contenedores/interfaces';
import { useCompanyCore } from '../../../../core/modules/company';
import { containersApiService } from '../../../../core/modules/container/services/api';
import { locationApiService } from '../../../../core/modules/location/services/api';
import { Container } from '../../../../core/interfaces/container.interface';
import { Location } from '../../../../core/interfaces/location.interface';

const ContainerAuditScreen = () => {
  const [audits, setAudits] = useState<ContainerAudit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [containerId, setContainerId] = useState<string>('');
  const [containerCode, setContainerCode] = useState<string>('');
  const [containers, setContainers] = useState<Container[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('lg'));
  const { companyId } = useCompanyCore();

  useEffect(() => {
    if (companyId) {
      containersApiService.getContainers(companyId, {}).then(res => setContainers(res.containers));
      locationApiService.getLocations(companyId).then(res => setLocations(res.locations));
    }
  }, [companyId]);

  const handleSearch = async (id?: number) => {
    const searchId = id || Number(containerId);
    if (!companyId || !searchId) {
      setError('Debe ingresar un ID de contenedor y tener una empresa seleccionada');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await fetchContainerAudits(companyId, searchId);
      // Mapear location_id a nombre
      const locationMap = Object.fromEntries(locations.map(l => [l.id, l.description]));
      setAudits(data.map(audit => ({
        ...audit,
        location: locationMap[audit.location] || audit.location
      })));
    } catch (e) {
      setError('Error al cargar auditoría de contenedores');
      setAudits([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAutocomplete = (_: any, value: Container | null) => {
    if (value) {
      setContainerId(value.id.toString());
      setContainerCode(value.code);
      handleSearch(value.id);
    }
  };

  return (
    <Page
      header={
        <div className="p-24 sm:p-32 w-full border-b-1 bg-white">
          <Typography className="text-24 md:text-32 font-extrabold tracking-tight leading-none">
            Auditoría de Contenedores
          </Typography>
        </div>
      }
      content={
        <Box className="w-full my-16 px-24 md:px-32">
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <TextField
              label="Código de Contenedor"
              value={containerCode}
              onChange={e => setContainerCode(e.target.value)}
              size="small"
              sx={{ minWidth: 180 }}
            />
            <Button variant="contained" onClick={() => {
              // Buscar solo por código
              if (containerCode) {
                const found = containers.find(c => c.code === containerCode);
                if (found) handleSearch(found.id);
                else setError('No se encontró contenedor con ese código');
              } else {
                setError('Ingrese el código de contenedor');
              }
            }} disabled={loading || !containerCode}>
              Buscar
            </Button>
          </Box>
          {loading ? (
            <Box className="flex justify-center items-center h-full"><CircularProgress /></Box>
          ) : error ? (
            <Alert severity="error" className="mb-16">{error}</Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><b>CÓDIGO DE CONTENEDOR</b></TableCell>
                    <TableCell><b>UBICACIÓN A LA QUE SE MOVIÓ</b></TableCell>
                    <TableCell><b>USUARIO</b></TableCell>
                    <TableCell><b>FECHA</b></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {audits.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No hay registros de auditoría
                      </TableCell>
                    </TableRow>
                  ) : (
                    audits.map((audit, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{audit.containerCode}</TableCell>
                        <TableCell>{audit.location}</TableCell>
                        <TableCell>{audit.user}</TableCell>
                        <TableCell>{new Date(audit.date).toLocaleString()}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      }
      scroll={isMobile ? 'normal' : 'content'}
    />
  );
};

export default ContainerAuditScreen; 